import styled, { createGlobalStyle, keyframes } from 'styled-components'
import theme from '@app/ui/theme'
import { fieldInputStyles } from '@app/components/molecules/FormFields/styled'

export const FieldWrapper = styled.div`
  width: 100%;

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }
`

export const TriggerWrapper = styled.div`
  position: relative;
  width: 100%;
`

export const TriggerInput = styled.input<{ hasError?: boolean }>`
  /* The same style as the typed-in fields, so the date sits in the column
     looking like the rest of them. The width comes from the row it is in. */
  ${fieldInputStyles}
  width: 100%;
  /* Room for the calendar icon on the right, matched on the left so the date
     stays centred the way the other fields are. */
  padding: 0 32px;
  border-color: ${(props) =>
    props.hasError ? theme.colors.warning : theme.colors.dark1};
  cursor: pointer;
`

export const TriggerIcon = styled.span`
  position: absolute;
  top: 0;
  right: 14px;
  display: flex;
  align-items: center;
  /* Matches the field height, which is what centres the icon against the
     date it sits next to. */
  height: 36px;
  pointer-events: none;
`

export const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 4px 2px 4px;
`

export const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: ${theme.colors.primary};
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.light2};
  }

  &:disabled {
    color: #d6dae0;
    cursor: default;
  }
`

export const HeaderSelects = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const HeaderSelect = styled.select`
  -webkit-appearance: none;
  appearance: none;
  padding: 5px 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.black};
  text-align: center;
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:hover,
  &:focus {
    outline: none;
    background-color: ${theme.colors.light2};
  }
`

const appear = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Everything here is scoped to the two class names this field sets, so the
// other date pickers in the app keep the look they have.
export const CalendarStyles = createGlobalStyle`
  .react-datepicker-popper.mi-datepicker-popper {
    z-index: 2000;
  }

  .react-datepicker-popper.mi-datepicker-popper[data-placement^='bottom'] {
    padding-top: 8px;
  }

  .react-datepicker-popper.mi-datepicker-popper[data-placement^='top'] {
    padding-bottom: 8px;
  }

  .react-datepicker.mi-datepicker {
    padding: 8px;
    border: none;
    border-radius: 14px;
    background-color: ${theme.colors.white};
    box-shadow: 0 18px 40px rgba(16, 24, 40, 0.18),
      0 2px 6px rgba(16, 24, 40, 0.06);
    font-family: 'Roboto', sans-serif;
    animation: ${appear} 0.14s ease-out;
  }

  .mi-datepicker .react-datepicker__header {
    padding: 0;
    border-bottom: none;
    background-color: ${theme.colors.white};
  }

  .mi-datepicker .react-datepicker__day-names {
    display: flex;
    margin: 10px 0 2px 0;
  }

  .mi-datepicker .react-datepicker__day-name {
    width: 38px;
    margin: 0 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #8a8f98;
  }

  .mi-datepicker .react-datepicker__month {
    margin: 0;
  }

  .mi-datepicker .react-datepicker__week {
    display: flex;
  }

  .mi-datepicker .react-datepicker__day {
    width: 38px;
    height: 38px;
    margin: 2px;
    border-radius: 10px;
    line-height: 38px;
    font-size: 14px;
    font-weight: 500;
    color: ${theme.colors.black};
    transition: background-color 0.12s ease, color 0.12s ease;
  }

  .mi-datepicker .react-datepicker__day:hover {
    background-color: ${theme.colors.light2};
  }

  .mi-datepicker .react-datepicker__day--outside-month {
    color: #c3c8d0;
  }

  .mi-datepicker .react-datepicker__day--today {
    font-weight: 700;
    box-shadow: inset 0 0 0 1px ${theme.colors.light};
  }

  /* The same blue the primary buttons use, flat and without a glow so the two
     read as one colour. The day the calendar opens on gets that blue too, so
     the highlight does not change colour once you click it. */
  .mi-datepicker .react-datepicker__day--keyboard-selected,
  .mi-datepicker .react-datepicker__day--keyboard-selected:hover,
  .mi-datepicker .react-datepicker__day--selected,
  .mi-datepicker .react-datepicker__day--selected:hover {
    background-color: ${theme.colors.secondary2};
    color: ${theme.colors.white};
  }

  .mi-datepicker .react-datepicker__day--disabled {
    color: #d6dae0;
  }

  .mi-datepicker .react-datepicker__day--disabled:hover {
    background-color: transparent;
  }

  .mi-datepicker .react-datepicker__today-button {
    margin-top: 6px;
    padding: 10px 0;
    border-top: 1px solid ${theme.colors.light2};
    background-color: transparent;
    font-size: 14px;
    font-weight: 600;
    color: ${theme.colors.secondary2};
  }

  /* On a phone the calendar is centred on top of the page instead of hanging
     off the field, which is the one placement that always fits. */
  div.react-datepicker__portal {
    padding: 16px;
    background-color: rgba(16, 24, 40, 0.55);
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
  }

  .react-datepicker__portal .mi-datepicker .react-datepicker__day,
  .react-datepicker__portal .mi-datepicker .react-datepicker__day-name {
    width: 38px;
    height: 38px;
    line-height: 38px;
    font-size: 14px;
  }

  .react-datepicker__portal .mi-datepicker .react-datepicker__day-name {
    height: auto;
    line-height: 20px;
    font-size: 11px;
  }
`
